import type { AxiosError } from 'axios';
import { apiClient } from '@/lib/api';
import { safetyService } from '@/features/safety/services/safety.service';
import { workforceService } from '@/features/workforce/services/workforce.service';
import { operationsService } from '@/features/operations/services/operations.service';
import { db } from './db';
import { offlineQueue } from './queue';
import type { QueueEntry, QueueResource, SyncStrategy } from './types';

/**
 * Sentinel URL prefix used by the offline-aware worker registration flow.
 * Forms persist photo Blobs in Dexie and substitute a sentinel like
 * `offline-attachment:42` into the payload. On sync, the strategy walks the
 * payload, uploads each referenced Blob to the real /uploads endpoint, and
 * replaces the sentinel with the returned server URL.
 */
export const OFFLINE_ATTACHMENT_PREFIX = 'offline-attachment:';

async function resolveOfflineAttachmentUrls(payload: Record<string, unknown>): Promise<void> {
  for (const key of Object.keys(payload)) {
    const value = payload[key];
    if (typeof value === 'string' && value.startsWith(OFFLINE_ATTACHMENT_PREFIX)) {
      const attachmentId = Number(value.slice(OFFLINE_ATTACHMENT_PREFIX.length));
      if (!Number.isFinite(attachmentId)) continue;
      const row = await db.attachments.get(attachmentId);
      if (!row) continue;
      const form = new FormData();
      form.append('file', row.blob, `${row.clientId}.jpg`);
      const { data } = await apiClient.post<{ url: string }>(
        '/workforce/uploads',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      payload[key] = data.url;
      await db.attachments.delete(attachmentId);
    }
  }
}

function isAxiosConflict(error: unknown): boolean {
  const ax = error as AxiosError | undefined;
  return Boolean(ax?.response && (ax.response.status === 409 || ax.response.status === 422));
}

function isPermanentHttpStatus(error: unknown): boolean {
  const ax = error as AxiosError | undefined;
  const status = ax?.response?.status;
  if (!status) return false;
  // 4xx (except 408, 425, 429) are not retriable
  if (status >= 400 && status < 500) {
    return ![408, 425, 429].includes(status);
  }
  return false;
}

/**
 * Upload all attachment blobs referenced by an entry and return a payload
 * patched with the resulting URLs. The shape of `attachments[i]` is:
 *   - `payloadField`: dotted path into the payload (e.g. "photoUrl" or "checklistResults")
 *   - `payloadIndex`: when the field is an array of objects, this is the index;
 *     the upload URL is then set as `payload[field][index].photoUrl`.
 */
async function uploadAttachmentsAndPatchPayload<P>(entry: QueueEntry): Promise<P> {
  const payload = structuredClone(entry.payload) as Record<string, unknown>;
  if (!entry.attachments?.length) return payload as unknown as P;

  for (const ref of entry.attachments) {
    const att = await offlineQueue.getAttachment(ref.attachmentId);
    if (!att) continue;
    const form = new FormData();
    form.append('file', att.blob, `${entry.clientId}.jpg`);
    // Reuses the workforce uploads endpoint — the only image-upload endpoint
    // currently exposed by the API. Same response shape for any feature.
    const { data } = await apiClient.post<{ url: string }>('/workforce/uploads', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (ref.payloadIndex !== undefined && Array.isArray(payload[ref.payloadField])) {
      const arr = payload[ref.payloadField] as Record<string, unknown>[];
      const target = arr[ref.payloadIndex];
      if (target) target['photoUrl'] = data.url;
    } else {
      payload[ref.payloadField] = data.url;
    }
  }
  return payload as unknown as P;
}

const safetyIncident: SyncStrategy = {
  resource: 'safety.incident.create',
  async uploadAttachments(entry) {
    return uploadAttachmentsAndPatchPayload(entry);
  },
  async submit(payload) {
    await safetyService.createIncident(payload as never);
  },
  isAlreadyAccepted: isAxiosConflict,
  isPermanentFailure: isPermanentHttpStatus,
};

const safetyInspection: SyncStrategy = {
  resource: 'safety.inspection.create',
  async uploadAttachments(entry) {
    return uploadAttachmentsAndPatchPayload(entry);
  },
  async submit(payload) {
    await safetyService.createInspection(payload as never);
  },
  isAlreadyAccepted: isAxiosConflict,
  isPermanentFailure: isPermanentHttpStatus,
};

const safetyHazard: SyncStrategy = {
  resource: 'safety.hazard.create',
  async uploadAttachments(entry) {
    return uploadAttachmentsAndPatchPayload(entry);
  },
  async submit(payload) {
    await safetyService.createHazard(payload as never);
  },
  isAlreadyAccepted: isAxiosConflict,
  isPermanentFailure: isPermanentHttpStatus,
};

const safetyCorrectiveAction: SyncStrategy = {
  resource: 'safety.corrective-action.create',
  async submit(payload) {
    await safetyService.createCorrectiveAction(payload as never);
  },
  isAlreadyAccepted: isAxiosConflict,
  isPermanentFailure: isPermanentHttpStatus,
};

const safetyCorrectiveActionStatus: SyncStrategy = {
  resource: 'safety.corrective-action.update-status',
  async submit(payload) {
    const { id, status } = payload as { id: string; status: string };
    await safetyService.updateCorrectiveActionStatus(id, status);
  },
  isPermanentFailure: isPermanentHttpStatus,
};

const attendanceCreateSession: SyncStrategy = {
  resource: 'workforce.attendance.create-session',
  async submit(payload) {
    await workforceService.createAttendanceSession(payload as never);
  },
  isAlreadyAccepted: isAxiosConflict,
  isPermanentFailure: isPermanentHttpStatus,
};

const attendanceCheckIn: SyncStrategy = {
  resource: 'workforce.attendance.check-in',
  async submit(payload) {
    const { sessionId, workerId } = payload as { sessionId: string; workerId: string };
    await workforceService.checkInWorker(sessionId, workerId);
  },
  isAlreadyAccepted: isAxiosConflict,
  isPermanentFailure: isPermanentHttpStatus,
};

const attendanceCheckOut: SyncStrategy = {
  resource: 'workforce.attendance.check-out',
  async submit(payload) {
    const { sessionId, workerId } = payload as { sessionId: string; workerId: string };
    await workforceService.checkOutWorker(sessionId, workerId);
  },
  isAlreadyAccepted: isAxiosConflict,
  isPermanentFailure: isPermanentHttpStatus,
};

const attendanceCloseSession: SyncStrategy = {
  resource: 'workforce.attendance.close-session',
  async submit(payload) {
    const { sessionId } = payload as { sessionId: string };
    await workforceService.closeAttendanceSession(sessionId);
  },
  isPermanentFailure: isPermanentHttpStatus,
};

const attendanceAddWorkers: SyncStrategy = {
  resource: 'workforce.attendance.add-workers',
  async submit(payload) {
    const { sessionId, workerIds } = payload as { sessionId: string; workerIds: string[] };
    await workforceService.addWorkersToSession(sessionId, workerIds);
  },
  isAlreadyAccepted: isAxiosConflict,
  isPermanentFailure: isPermanentHttpStatus,
};

const attendanceRemoveWorker: SyncStrategy = {
  resource: 'workforce.attendance.remove-worker',
  async submit(payload) {
    const { sessionId, workerId } = payload as { sessionId: string; workerId: string };
    await workforceService.removeWorkerFromSession(sessionId, workerId);
  },
  isPermanentFailure: isPermanentHttpStatus,
};

const workerRegister: SyncStrategy = {
  resource: 'workforce.worker.register',
  async uploadAttachments(entry) {
    // Resolve any `offline-attachment:<id>` sentinels in the payload by
    // uploading the persisted Blob and replacing the value with the real URL.
    const payload = structuredClone(entry.payload) as Record<string, unknown>;
    await resolveOfflineAttachmentUrls(payload);
    return payload;
  },
  async submit(payload) {
    await workforceService.registerWorker(payload as never);
  },
  isAlreadyAccepted: isAxiosConflict,
  isPermanentFailure: isPermanentHttpStatus,
};

const operationsMeu: SyncStrategy = {
  resource: 'operations.meu.create',
  async submit(payload) {
    await operationsService.createMeu(payload as never);
  },
  isAlreadyAccepted: isAxiosConflict, // FR-TRACE-004 server-side dedup by clientId
  isPermanentFailure: isPermanentHttpStatus,
};

const operationsCustodyTransfer: SyncStrategy = {
  resource: 'operations.custody-transfer.create',
  async submit(payload) {
    await operationsService.createCustodyTransfer(payload as never);
  },
  isAlreadyAccepted: isAxiosConflict,
  isPermanentFailure: isPermanentHttpStatus,
};

// Partial record — Dexie storage is untrusted: an older app version could
// have queued a `resource` value that no longer maps to a strategy. The
// SyncManager treats a missing strategy as a permanent failure.
export const strategyRegistry: Partial<Record<QueueResource, SyncStrategy>> = {
  'safety.incident.create': safetyIncident,
  'safety.inspection.create': safetyInspection,
  'safety.hazard.create': safetyHazard,
  'safety.corrective-action.create': safetyCorrectiveAction,
  'safety.corrective-action.update-status': safetyCorrectiveActionStatus,
  'workforce.attendance.create-session': attendanceCreateSession,
  'workforce.attendance.check-in': attendanceCheckIn,
  'workforce.attendance.check-out': attendanceCheckOut,
  'workforce.attendance.close-session': attendanceCloseSession,
  'workforce.attendance.add-workers': attendanceAddWorkers,
  'workforce.attendance.remove-worker': attendanceRemoveWorker,
  'workforce.worker.register': workerRegister,
  'operations.meu.create': operationsMeu,
  'operations.custody-transfer.create': operationsCustodyTransfer,
};
